// Display helpers for ride-hailing (TRV10) — vehicle labels, RIDE-state copy,
// status badge variants, and rider cancel-reason options.
import type { BadgeVariant } from './format';

const VEHICLE_LABELS: Record<string, string> = {
  AUTO_RICKSHAW: 'Auto',
  CAB: 'Cab',
  TWO_WHEELER: 'Bike',
  SEDAN: 'Sedan',
  SUV: 'SUV',
  HATCHBACK: 'Hatchback',
};

/** Friendly vehicle label from a category/variant (variant wins when known). */
export function vehicleLabel(category?: string, variant?: string): string {
  if (variant && VEHICLE_LABELS[variant]) return VEHICLE_LABELS[variant];
  if (category && VEHICLE_LABELS[category]) return VEHICLE_LABELS[category];
  if (category) return category.replace(/_/g, ' ').toLowerCase();
  return 'Ride';
}

/** Humanize ONDC quote breakup titles for the rider-facing fare card. */
export function rideFareLineLabel(title: string): string {
  switch (title) {
    case 'BASE_FARE':
    case 'BASE_PRICE':
      return 'Base fare';
    case 'DISTANCE_FARE':
      return 'Distance fare';
    case 'BUYER_ADDITIONAL_AMOUNT':
      return 'Tip';
    case 'TAX':
      return 'Taxes';
    case 'TOLL':
      return 'Toll';
    case 'OFFER':
      return 'Discount';
    default:
      return title
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/^\w/, (character) => character.toUpperCase());
  }
}

const RIDE_STATE_LABELS: Record<string, string> = {
  RIDE_CONFIRMED: 'Booking confirmed',
  RIDE_ASSIGNED: 'Driver assigned',
  RIDE_ENROUTE_PICKUP: 'Driver on the way',
  RIDE_ARRIVED_PICKUP: 'Driver has arrived',
  RIDE_STARTED: 'On the way',
  RIDE_ENDED: 'Trip complete',
  RIDE_CANCELLED: 'Cancelled',
};

/** Canonicalize provider variants before using them for display logic. */
export function normalizeRideState(state?: string): string | undefined {
  return state === 'RIDE_ARRIVED' ? 'RIDE_ARRIVED_PICKUP' : state;
}

/** Do not present terminal completion until the final on_update has converged. */
export function visibleRideState(state?: string, statusReady = false): string | undefined {
  const normalized = normalizeRideState(state);
  return normalized === 'RIDE_ENDED' && !statusReady ? 'RIDE_STARTED' : normalized;
}

/** Human copy for a RIDE_* fulfillment state. */
export function rideStateLabel(state?: string): string {
  const normalized = normalizeRideState(state);
  if (!normalized) return '';
  return (
    RIDE_STATE_LABELS[normalized] ??
    normalized
      .replace(/^RIDE_/, '')
      .replace(/_/g, ' ')
      .toLowerCase()
  );
}

/** Short status text for a ride (lifecycle state preferred over order status). */
export function rideStatusText(rideState?: string, status?: string): string {
  if (rideState) return rideStateLabel(rideState);
  return status ?? '';
}

/** Map a ride lifecycle/order state to a Badge variant. */
export function rideStatusVariant(rideState?: string, status?: string): BadgeVariant {
  switch (normalizeRideState(rideState) || status) {
    case 'RIDE_ENDED':
    case 'COMPLETED':
      return 'green';
    case 'RIDE_CANCELLED':
    case 'CANCELLED':
      return 'red';
    case 'RIDE_STARTED':
    case 'RIDE_ARRIVED_PICKUP':
      return 'primary';
    case 'SOFT_CANCEL':
      return 'amber';
    default:
      return 'blue';
  }
}

/** Rider-facing cancel reason codes (TRV10 ride-hailing set). */
export const RIDE_CANCEL_REASONS: { code: string; label: string }[] = [
  { code: '005', label: 'Booked by mistake' },
  { code: '001', label: 'Driver not moving' },
  { code: '002', label: "Can't contact the driver" },
  { code: '004', label: 'Pickup location is wrong' },
  { code: '003', label: 'Driver asked me to cancel' },
];
